/* Public sign + approve pages - translations
   ============================================
   Used by /sign/<token> (sign.html + src/sign.ts) and /approve/<token>
   (approve.html + src/approve.ts). Strings are recipient-facing, so the
   wording is task-focused: "open the guideline", "enter the code we
   emailed you", "approve / reject this request". The pages are minimal
   (no full public nav) so the key set is small.
*/

/* eslint-disable @typescript-eslint/no-explicit-any */
export const nl = {
  sign: {
    title:           'Richtlijn ondertekenen',
    h1:              'Richtlijn ondertekenen',
    lede:            'Open de richtlijn hieronder, voer de 6-cijferige code in die we per e-mail hebben gestuurd, en bevestig de gegevens om door te gaan.',
    loading:         'Laden…',
    invalid_link:    'Deze ondertekenlink is niet (meer) geldig. Vraag een nieuwe link op via de beheerder.',
    already_accepted:'Deze richtlijn is al ondertekend op {at}.',
    meta_project:    'Project',
    meta_organisation:'Organisatie',
    meta_guideline:  'Richtlijn',
    meta_signer:     'Ontvanger',
    meta_expires:    'Verloopt op',
    pdf_heading:     'Richtlijn',
    pdf_fallback:    'Kan de PDF niet inline tonen? Open hem in een nieuw tabblad:',
    pdf_open:        'Open de richtlijn',
    form_heading:    'Bevestig en onderteken',
    form_lede:       'Vul de 6-cijferige code in die we per e-mail hebben gestuurd, en de gegevens van de aansluiter.',
    label_code:      '6-cijferige code',
    label_signer_name:'Naam aansluiter',
    label_signer_phone:'Telefoon (optioneel)',
    label_signer_organisation:'Organisatie aansluiter',
    label_project_start_date:'Voorziene startdatum aansluiting',
    label_assigned_by_name:'In opdracht van - naam',
    label_assigned_by_role:'In opdracht van - rol (optioneel)',
    label_accept:    'Ik heb de richtlijn gelezen en accepteer hem.',
    submit:          'Bevestig & onderteken',
    err_required:    'Vul alle verplichte velden in.',
    err_code_format: 'Code is 6 cijfers.',
    err_accept_required:'Vink het akkoord aan om door te gaan.',
    err_code_incorrect:'Code is onjuist. Probeer het opnieuw.',
    err_too_many_attempts:'Te veel onjuiste codes. Vraag een nieuwe link op bij de beheerder.',
    err_generic:     'Versturen mislukt. Probeer het later opnieuw.',
    success_h1:      'Richtlijn ondertekend',
    success_lede:    'Bedankt. We hebben de bevestiging genoteerd op {at}. U kunt dit tabblad sluiten.',
  },
  approve: {
    title:               'Aanvraag beoordelen',
    h1:                  'Aanvraag beoordelen',
    loading:             'Laden…',
    invalid_link:        'Deze beoordelingslink is niet (meer) geldig. Neem contact op met de beheerder voor een nieuwe link.',
    recipient_operator:  'Als beheerder van het laadpunt wordt u gevraagd deze aanvraag goed te keuren.',
    recipient_municipality:'Als gemeente wordt u gevraagd deze aanvraag goed te keuren.',
    meta_project:        'Project',
    meta_organisation:   'Organisatie',
    meta_recipient:      'Ontvanger',
    meta_expires:        'Verloopt op',
    btn_approve:         'Goedkeuren',
    btn_reject:          'Afwijzen',
    note_label:          'Toelichting (verplicht bij afwijzing, anders optioneel)',
    note_placeholder:    'Bijvoorbeeld: te grote impact op andere gebruikers.',
    err_reject_note:     'Geef een toelichting bij een afwijzing.',
    err_generic:         'Versturen mislukt. Probeer het later opnieuw.',
    success_approved_h1: 'Goedkeuring genoteerd',
    success_rejected_h1: 'Afwijzing genoteerd',
    success_lede:        'Bedankt. Uw beslissing is op {at} vastgelegd. U kunt dit tabblad sluiten.',
  },
};

export const en = {
  sign: {
    title:           'Sign the guideline',
    h1:              'Sign the guideline',
    lede:            'Open the guideline below, enter the 6-digit code we emailed you, and confirm the details to continue.',
    loading:         'Loading…',
    invalid_link:    'This signing link is no longer valid. Request a fresh link from the administrator.',
    already_accepted:'This guideline has already been signed on {at}.',
    meta_project:    'Project',
    meta_organisation:'Organisation',
    meta_guideline:  'Guideline',
    meta_signer:     'Recipient',
    meta_expires:    'Expires on',
    pdf_heading:     'Guideline',
    pdf_fallback:    "Can't view the PDF inline? Open it in a new tab:",
    pdf_open:        'Open the guideline',
    form_heading:    'Confirm and sign',
    form_lede:       'Enter the 6-digit code we emailed you, plus the connector details.',
    label_code:      '6-digit code',
    label_signer_name:'Connector name',
    label_signer_phone:'Phone (optional)',
    label_signer_organisation:'Connector organisation',
    label_project_start_date:'Planned start date of the connection',
    label_assigned_by_name:'On behalf of - name',
    label_assigned_by_role:'On behalf of - role (optional)',
    label_accept:    'I have read the guideline and accept it.',
    submit:          'Confirm & sign',
    err_required:    'Please fill every required field.',
    err_code_format: 'The code is 6 digits.',
    err_accept_required:'Tick the acceptance box to continue.',
    err_code_incorrect:'The code is incorrect. Please try again.',
    err_too_many_attempts:'Too many incorrect attempts. Request a fresh link from the administrator.',
    err_generic:     'Submission failed. Please try again later.',
    success_h1:      'Guideline signed',
    success_lede:    'Thank you. We recorded the acceptance on {at}. You can close this tab.',
  },
  approve: {
    title:               'Review the request',
    h1:                  'Review the request',
    loading:             'Loading…',
    invalid_link:        'This approval link is no longer valid. Contact the administrator for a fresh link.',
    recipient_operator:  'As the charge-point operator you are asked to approve this request.',
    recipient_municipality:'As the municipality you are asked to approve this request.',
    meta_project:        'Project',
    meta_organisation:   'Organisation',
    meta_recipient:      'Recipient',
    meta_expires:        'Expires on',
    btn_approve:         'Approve',
    btn_reject:          'Reject',
    note_label:          'Note (required when rejecting, otherwise optional)',
    note_placeholder:    'e.g. too much impact on other users.',
    err_reject_note:     'A note is required when rejecting.',
    err_generic:         'Submission failed. Please try again later.',
    success_approved_h1: 'Approval recorded',
    success_rejected_h1: 'Rejection recorded',
    success_lede:        'Thank you. Your decision was recorded on {at}. You can close this tab.',
  },
};
